import { IonButton } from '@ionic/react'
import { RadioGroup } from '@headlessui/react'
import { Controller } from 'react-hook-form'
import { ErrorMessage } from '@hookform/error-message'

import { StyledError } from '../../../components/ui/form/errors'
import { TextInput, NumericInput } from '../../../components/ui/form/input'
import { SchedulePicker } from '../../../components/ui/form/schedule-picker'
import { useCreateLoyaltyBonusRule } from '../../../loyalty/mutations'
import { useCreateLoyaltyBonusForm } from '../../../loyalty/utils'

import type { LoyaltyBonusRuleInputs } from '../../../loyalty/loyalty.schema'
import type { BonusAvailability } from './types'

type Props = {
  availability: BonusAvailability
  onSubmit: (objectId: string) => void
}

function AddBonusForm({ availability, onSubmit }: Props) {
  const createBonusRule = useCreateLoyaltyBonusRule()

  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
    setValue,
    watchType,
  } = useCreateLoyaltyBonusForm()

  function handleCreateBonusRule(data: LoyaltyBonusRuleInputs) {
    createBonusRule.mutate(data, {
      onSuccess: (data) => onSubmit(data.loyaltyBonusRule.objectId),
    })
  }

  return (
    <form className="space-y-2" onSubmit={handleSubmit(handleCreateBonusRule)}>
      <Controller
        name="type"
        control={control}
        render={({ field: { value, onChange } }) => (
          <RadioGroup
            className="border-ion-color-step-950 flex items-center gap-5"
            value={value}
            onChange={(e) => {
              e === 'custom'
                ? setValue('isManual', true)
                : setValue('isManual', false)
              onChange(e)
            }}
          >
            <RadioGroup.Option
              value="dollar"
              className="flex cursor-pointer items-center rounded-full ui-disabled:cursor-default"
            >
              <span className="border-ion-color-primary ui-checked:bg-ion-color-yellow size-4 rounded-full border" />
              <span className="flex cursor-pointer items-center px-1.5 leading-3 ui-disabled:cursor-default">
                Dollar Amount
              </span>
            </RadioGroup.Option>
            <RadioGroup.Option
              value="custom"
              className="flex cursor-pointer items-center rounded-full ui-disabled:cursor-default"
            >
              <span className="border-ion-color-primary ui-checked:bg-ion-color-yellow size-4 rounded-full border" />
              <span className="flex cursor-pointer items-center px-1.5 leading-3 ui-disabled:cursor-default">
                Custom
              </span>
            </RadioGroup.Option>
          </RadioGroup>
        )}
      />

      {watchType === 'custom' ? (
        <div className="flex items-end gap-x-2">
          <div className="flex-grow">
            <TextInput control={control} name="text" color="light" />
            <ErrorMessage as={StyledError} errors={errors} name="text" />
          </div>
          <span className="text-sm">(enter custom reward)</span>
        </div>
      ) : watchType === 'dollar' ? (
        <div className="flex items-center gap-x-2">
          <span className="text-lg">$</span>
          <div>
            <NumericInput control={control} name="value" color="light" />
            <ErrorMessage as={StyledError} errors={errors} name="value" />
          </div>
          <span className="text-lg">in rewards</span>
        </div>
      ) : null}

      <div className="py-3">
        <p className="text-lg">Bonus Availability:</p>

        {availability === 'expiration' ? (
          <>
            <div className="flex items-center gap-x-2">
              <span className="text-lg">Expires in</span>
              <NumericInput
                control={control}
                name="expirationPointsDays"
                color="light"
              />
              <span className="text-lg">days</span>
            </div>
            <ErrorMessage
              as={StyledError}
              errors={errors}
              name="expirationPointsDays"
            />
          </>
        ) : availability === 'range' ? (
          <>
            <div className="flex flex-col items-start gap-y-2">
              <div className="flex items-center gap-x-1.5">
                <span>Start Date:</span>
                <Controller
                  name="startDate"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <SchedulePicker
                      color="primary"
                      label="Choose a date"
                      value={value}
                      onChange={(e) => onChange(e.detail.value)}
                      onReset={() => {
                        onChange(null)
                      }}
                    />
                  )}
                />
              </div>
              <div className="flex items-center gap-x-1.5">
                <span>End Date:</span>
                <Controller
                  name="endDate"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <SchedulePicker
                      color="primary"
                      label="Choose a Date"
                      value={value}
                      onChange={(e) => onChange(e.detail.value)}
                      onReset={() => {
                        onChange(null)
                      }}
                    />
                  )}
                />
              </div>
            </div>
            <ErrorMessage as={StyledError} errors={errors} name="startDate" />
            <ErrorMessage as={StyledError} errors={errors} name="endDate" />
          </>
        ) : null}
      </div>

      <IonButton
        color="yellow"
        size="small"
        type="submit"
        disabled={isSubmitting}
      >
        Save
      </IonButton>
    </form>
  )
}

export { AddBonusForm }
